// Tela de Lançar OS (issue #9; Fase 6 — Ficha Única). Lista os Lotes com os
// filtros de sempre (Situação, Cliente, busca) ao lado de um painel que
// mostra sempre a mesma coisa: a OS selecionada (ou uma nova), com Coleta e
// Fechamento na mesma ficha — nunca um modal.

import {
  rotuloSituacao,
  situacaoLote,
  totalRecebido,
  type Lote,
} from "../../domain/lotes.ts";
import { devolvidaEntregue } from "../../domain/backlog.ts";
import { saidasDoSublote } from "../../domain/devolucao.ts";
import { atrasoDoLote, descreveAtraso, previstoDoLote } from "../../domain/paradas.ts";
import { formatData, horaDe, paraISO } from "../../domain/datas.ts";
import type { Id } from "../../domain/ids.ts";
import type { Snapshot } from "../../domain/snapshot.ts";
import type { Store } from "../store.ts";
import { clear, el, openModal, painelRedimensionavel, tabelaPaginada, botao } from "../ui.ts";
import { toastErro } from "../toast.ts";
import {
  abrirConfirmacao,
  barraCadastro,
  campo,
  campoObrigatorio,
  filtroData,
  telaLista,
} from "./_cadastro.ts";
import { abrirImportarOS } from "./_os-import.ts";
import { painelSelecao } from "./_multi-select.ts";
import { memoriaDaFicha, NOVA_OS } from "./_ficha-digitacao.ts";
import { EstadoLista } from "./_estado-lista.ts";
import { buscaDaLista, filtroMultiplo } from "./_lista-filtros.ts";
import { criarAcaoComando } from "./_acao.ts";
import { montarFichaOS } from "./_ficha-os.ts";
import { podeEditarModulo } from "./_permissao.ts";
import { emCorrecao } from "../../domain/fechamento-do-lote.ts";

function formatDataHora(data: string, hora: string): string {
  return `${formatData(data)} ${hora}`;
}

export function renderLancarOS(
  container: HTMLElement,
  store: Store,
): () => void {
  let selecao: Id | "novo" | null = null;
  // Lembrado entre um "Salvar e lançar outra" e a Nova OS em branco que abre
  // em seguida — mesma remessa de trabalho, mesmo hotel na maioria das vezes.
  let clientePreSelecionado: Id | null = null;
  // filtros, busca e página num estado só (#93). "Reaberta" entra visível
  // por padrão junto de lançado/cancelado — é uma situação de exceção que pede
  // ação (Cancelar), não o repouso comum que "fechado"/"faturado" são (esses
  // ficam escondidos por padrão, decisão do dono). O "Limpar Filtros" volta a
  // esse padrão — antes marcava todas.
  const filtros = new EstadoLista({
    cliente: null as string[] | null,
    situacao: ["lancado", "reaberta", "cancelado"] as string[] | null,
    coletaDe: "",
    coletaAte: "",
  });
  const est = filtros.tabela;
  const busca = buscaDaLista(filtros, "Buscar por OS, cliente ou código…", () => render());
  const fSituacao = filtroMultiplo(filtros, "situacao", "Situação", () => render());
  const fCliente = filtroMultiplo(filtros, "cliente", "Cliente", () => render());

  // --------------------------------------------------------------- lista --
  // Devolve só a tabela paginada — a barra de filtros e a busca são montadas
  // uma vez em `render()` e envolvem este bloco (via `telaLista`), não o
  // contrário, porque a mesma barra também comanda "Nova OS"/"Importar OS".

  const montarListaLotes = (
    snap: Snapshot,
    lotes: Lote[],
    cliNome: Map<Id, string>,
    podeGravar: boolean,
  ): HTMLElement => {
    const agora = new Date();
    const linhas = lotes.map((l) => {
      const situacao = situacaoLote(snap, l.id);
      const previsto = previstoDoLote(snap, l);
      const atraso = atrasoDoLote(snap, l);
      const vencido =
        situacao === "lancado" && previsto != null && previsto < agora;
      const total = totalRecebido(snap, l.id);

      const abrirBtn = botao(
        "registro",
        l.id === selecao ? { class: "ativa" } : {},
        String(l.os),
      );
      abrirBtn.addEventListener("click", () => {
        selecao = l.id;
        render();
      });

      const abrir = botao("secundario", {}, "Abrir");
      abrir.addEventListener("click", () => {
        selecao = l.id;
        render();
      });
      const acoes: Node[] = [abrir];
      if (podeGravar && situacao === "lancado") {
        const apagar = botao("perigo", {}, "Excluir");
        apagar.addEventListener("click", () => {
          abrirConfirmacao({
            titulo: "Excluir esta OS?",
            mensagem: `A OS ${l.os} (${l.codigo}) e o que foi lançado nela saem do sistema. Não dá para desfazer.${avisoSaidas(l.id)}`,
            rotuloConfirmar: "Excluir",
            perigo: true,
            aoConfirmar: () =>
              void store.executar(
                { agregado: "lote", cmd: { tipo: "excluir", id: l.id } },
                "OS excluída.",
              ),
          });
        });
        acoes.push(apagar);
      } else if (podeGravar && (situacao === "fechado" || situacao === "reaberta")) {
        const cancelar = botao("secundario", {}, "Cancelar");
        cancelar.addEventListener("click", () => abrirCancelar(l));
        acoes.push(cancelar);
      }

      return el(
        "tr",
        situacao === "cancelado" ? { class: "inativo" } : {},
        el(
          "td",
          { "data-sort": String(l.os) },
          el(
            "span",
            { class: "os-cel" },
            abrirBtn,
            l.urgente ? el("span", { class: "pill-urgente" }, "Urgente") : null,
            l.relavagem ? el("span", { class: "pill-relavagem" }, "Relavagem") : null,
          ),
          el("small", { class: "muted os-cod" }, l.codigo),
        ),
        el("td", {}, cliNome.get(l.clienteId) ?? "—"),
        el(
          "td",
          { "data-sort": `${l.coletaData}${l.coletaHora}` },
          el("span", { class: "muted" }, formatDataHora(l.coletaData, l.coletaHora)),
          el(
            "small",
            { class: vencido ? "os-cod prazo-vencido" : "os-cod muted" },
            previsto == null
              ? "Sem Prazo"
              : `Prazo ${formatData(paraISO(previsto))} ${horaDe(previsto)}`,
          ),
          atraso && atraso.atrasado
            ? el(
                "small",
                { class: "os-cod prazo-vencido" },
                `Atraso ${descreveAtraso(atraso.minutos)}`,
              )
            : null,
        ),
        el("td", { class: "muted num" }, l.sacos == null ? "—" : String(l.sacos)),
        el(
          "td",
          { class: "num", "data-sort": String(total) },
          total === 0 ? "—" : String(total),
          situacao === "fechado" || situacao === "reaberta"
            ? el("small", { class: "muted" }, ` → ${devolvidaEntregue(snap, l.id)}`)
            : null,
        ),
        el(
          "td",
          {},
          el("span", { class: `pill-situacao pill-situacao-${situacao}` }, rotuloSituacao(situacao)),
        ),
        el("td", { class: "acoes" }, ...acoes),
      );
    });

    return tabelaPaginada(
      "lotes",
      [
        { t: "OS", w: 145 },
        "Cliente",
        { t: "Coleta e prazo", w: 168 },
        { t: "Sacos", w: 56 },
        { t: "Recebidas", w: 100 },
        { t: "Situação", w: 124 },
        { t: "", w: 172, ord: false as const },
      ],
      linhas,
      est,
      render,
    );
  };

  /** O que excluir/cancelar vai refazer no Backlog (ADR-0026), em texto. */
  const avisoSaidas = (loteId: string): string => {
    const snap = store.get();
    const { lotes, perdas } = saidasDoSublote(snap, loteId);
    if (lotes.length === 0 && perdas === 0) return "";
    const oss = lotes.map((id) => snap.lotes.find((x) => x.id === id)?.os).join(", ");
    const partes = [
      ...(lotes.length > 0
        ? [`a OS ${oss} levou peças retidas nesta OS e será recalculada (as peças voltam a sair do Backlog)`]
        : []),
      ...(perdas > 0 ? [`${perdas} perda(s) registrada(s) contra as retidas desta OS serão apagadas`] : []),
    ];
    return ` Atenção: ${partes.join("; ")}.`;
  };

  // ---------------------------------------------------------- cancelar ----

  const abrirCancelar = (l: Lote): void => {
    openModal(`Cancelar a OS ${l.os}`, (fechar) => {
      const erro = el("p", { class: "form-erro", role: "alert" });
      const motivo = el("textarea", {
        rows: "3",
        placeholder: "Por que esta OS está sendo cancelada?",
      }) as HTMLTextAreaElement;
      const voltar = botao("secundario", {}, "Voltar");
      voltar.addEventListener("click", fechar);
      const ok = botao("primario", { type: "submit" }, "Cancelar OS");

      const form = el(
        "form",
        { class: "form-modal" },
        el(
          "p",
          { class: "muted" },
          "A OS fica no histórico, fora dos números. O motivo é obrigatório.",
        ),
        avisoSaidas(l.id) ? el("p", { class: "aviso aviso-atencao" }, avisoSaidas(l.id).trim()) : null,
        campoObrigatorio("Motivo", motivo),
        erro,
        el("div", { class: "modal-actions" }, voltar, ok),
      );
      form.addEventListener("submit", (ev) => {
        ev.preventDefault();
        erro.textContent = "";
        criarAcaoComando(store, erro)(
          { agregado: "lote", cmd: { tipo: "cancelar", id: l.id, motivo: motivo.value } },
          "OS cancelada.",
          fechar,
        );
      });
      return form;
    });
  };

  // --------------------------------------------------------------- render --

  const render = (): void => {
    clear(container);
    const snap = store.get();
    const podeGravar = podeEditarModulo(store, "operacional");
    const cliNome = new Map(snap.clientes.map((c) => [c.id, c.nome]));
    const clientesAtivos = [...snap.clientes]
      .filter((c) => c.ativo)
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

    if (selecao !== "novo" && selecao !== null && !snap.lotes.some((l) => l.id === selecao)) {
      selecao = null;
    }
    const loteAtual = selecao === "novo" || selecao === null ? null : snap.lotes.find((l) => l.id === selecao)!;

    fCliente.atualizar(clientesAtivos.map((c) => ({ id: c.id, nome: c.nome })));
    // Mesmo rótulo do selo da lista: a Situação escrita de um jeito só.
    fSituacao.atualizar(
      (["lancado", "fechado", "faturado", "reaberta", "cancelado"] as const).map((id) => ({ id, nome: rotuloSituacao(id) })),
    );
    const cliFiltro = fCliente.selecionados();
    const sitFiltro = fSituacao.selecionados();
    const fColetaDe = filtros.filtro("coletaDe");
    const fColetaAte = filtros.filtro("coletaAte");
    const lotes = snap.lotes
      .filter((l) => cliFiltro.size === 0 || cliFiltro.has(l.clienteId))
      .filter((l) =>
        sitFiltro.size === 0 ? true : sitFiltro.has(situacaoLote(snap, l.id)),
      )
      .filter((l) => fColetaDe === "" || l.coletaData >= fColetaDe)
      .filter((l) => fColetaAte === "" || l.coletaData <= fColetaAte)
      .filter((l) =>
        busca.casa(
          String(l.os),
          cliNome.get(l.clienteId),
          l.codigo,
          formatData(l.coletaData),
        ),
      )
      .sort((a, b) => {
        const c = (cliNome.get(a.clienteId) ?? "").localeCompare(
          cliNome.get(b.clienteId) ?? "",
          "pt-BR",
        );
        return c !== 0
          ? c
          : `${b.coletaData}${b.coletaHora}`.localeCompare(
              `${a.coletaData}${a.coletaHora}`,
            );
      });

    const importar = botao("secundario", {}, "Importar OS");
    importar.addEventListener("click", () => abrirImportarOS(store));

    const fecharMassa = botao("secundario", {}, "Fechar OS em Massa");
    fecharMassa.addEventListener("click", () => {
      if (!osFechaveisEmMassa(snap).length) {
        toastErro("Nenhuma OS lançada com peças recebidas para fechar.");
        return;
      }
      abrirFecharEmMassa(store);
    });

    const barra = barraCadastro(
      [
        fCliente.element,
        fSituacao.element,
        filtroData("Coleta de", fColetaDe, (v) => {
          filtros.mudarFiltro("coletaDe", v);
          render();
        }),
        filtroData("Coleta até", fColetaAte, (v) => {
          filtros.mudarFiltro("coletaAte", v);
          render();
        }),
      ],
      "Nova OS",
      () => {
        if (clientesAtivos.length === 0) {
          toastErro("Cadastre um cliente ativo antes de lançar uma OS.");
          return;
        }
        clientePreSelecionado = null;
        selecao = "novo";
        // "Nova OS" clicado: começa do zero, não do que sobrou de outra
        memoriaDaFicha.esquecer(NOVA_OS);
        render();
      },
      () => {
        filtros.limpar();
        render();
      },
      podeGravar ? [importar, fecharMassa] : [],
      podeGravar,
    );

    const listaBloco = telaLista(
      barra,
      busca.elemento(),
      lotes.length === 0
        ? el(
            "p",
            { class: "muted lista-vazia" },
            snap.lotes.length === 0
              ? "Nenhuma OS lançada ainda."
              : "Nenhuma OS com esses filtros.",
          )
        : montarListaLotes(snap, lotes, cliNome, podeGravar),
    );

    const layout = el("div", { class: "lancar-os-layout" }, listaBloco);

    if (selecao !== null) {
      const fichaEl = montarFichaOS(
        store,
        loteAtual,
        {
          aoLancarNova: (loteId, opts) => {
            if (opts?.lancarOutra) {
              clientePreSelecionado = opts.clienteId ?? null;
              selecao = "novo";
            } else {
              selecao = loteId;
            }
            render();
          },
          aoFechar: () => {
            // fechar a Nova OS sem lançar descarta o que foi digitado nela
            if (selecao === "novo") memoriaDaFicha.esquecer(NOVA_OS);
            clientePreSelecionado = null;
            selecao = null;
            render();
          },
        },
        selecao === "novo" ? (clientePreSelecionado ?? undefined) : undefined,
      );
      const divisoria = painelRedimensionavel({ chave: "lancar-os", painel: fichaEl });
      layout.append(divisoria, fichaEl);
      // No celular a ficha empilha abaixo da lista (media query em
      // styles.css) — rola até ela pra quem acabou de tocar em "Abrir"/
      // "Nova OS" não precisar procurar.
      if (window.innerWidth <= 900) {
        requestAnimationFrame(() => fichaEl.scrollIntoView({ behavior: "smooth", block: "start" }));
      }
    }

    container.append(layout);
  };

  render();
  return store.subscribe(render);
}

// -------------------------------------------------------- fechar em massa --

/**
 * As OS que podem entrar no fechamento em massa: lançadas, com pelo menos
 * uma peça Recebida — sem isso não há o que copiar pra Devolvida/Conferida.
 */
function osFechaveisEmMassa(snap: Snapshot): Lote[] {
  return snap.lotes.filter(
    (l) =>
      situacaoLote(snap, l.id) === "lancado" &&
      !emCorrecao(l) && // reaberta para correção: fecha pela Ficha
      totalRecebido(snap, l.id) > 0,
  );
}

/**
 * Fecha várias OS de uma vez, assumindo Devolvida = Conferida = Recebida em
 * cada uma — o mesmo que "Preencher conforme comanda" faz numa OS por vez.
 * Cada OS usa a própria Recebida e o próprio prazo previsto; não há um campo
 * de data único pro lote inteiro (`comandos/devolucao.ts`,
 * `registrarEmMassa`). Mesmo padrão visual de "Abrir faturas em massa"
 * (Faturamento): `painelSelecao` + contador + resumo pelo diff do Snapshot.
 */
function abrirFecharEmMassa(store: Store): void {
  openModal("Fechar OS em Massa", (fechar) => {
    const snap = store.get();
    const cliNome = new Map(snap.clientes.map((c) => [c.id, c.nome]));
    const elegiveis = [...osFechaveisEmMassa(snap)].sort((a, b) => a.os - b.os);

    const erro = el("p", { class: "form-erro", role: "alert" });
    const fecharBtn = botao(
      "primario",
      {},
      "Fechar",
    ) as HTMLButtonElement;
    fecharBtn.disabled = true;

    const contador = el("span", { class: "selecao-contador" });
    const atualizar = (): void => {
      const n = painel.selecionados().length;
      contador.textContent = `${n} de ${elegiveis.length} selecionada(s)`;
      fecharBtn.textContent = n > 0 ? `Fechar ${n} OS` : "Fechar";
      fecharBtn.disabled = n === 0;
    };
    const painel = painelSelecao({
      itens: elegiveis.map((l) => ({
        id: l.id,
        nome: `OS ${l.os} · ${cliNome.get(l.clienteId) ?? "—"} · ${totalRecebido(snap, l.id)} peça(s)`,
      })),
      buscaPlaceholder: "Buscar OS ou cliente…",
      aoMudar: () => atualizar(),
    });
    atualizar();

    const corpo = el("div", { class: "form-modal" });

    const mostrarResumo = (
      fechadas: number,
      puladas: { os: number; motivo: string }[],
    ): void => {
      clear(corpo);
      corpo.append(el("p", { class: "aviso aviso-sucesso" }, `${fechadas} OS fechada(s).`));
      if (puladas.length > 0) {
        corpo.append(
          el("p", { class: "muted" }, `${puladas.length} OS não fechada(s):`),
          el(
            "table",
            { class: "mini" },
            el(
              "tbody",
              {},
              ...puladas.map((p) =>
                el(
                  "tr",
                  {},
                  el("td", {}, String(p.os)),
                  el("td", { class: "muted" }, p.motivo),
                ),
              ),
            ),
          ),
        );
      }
      const concluir = botao("primario", {}, "Concluir");
      concluir.addEventListener("click", fechar);
      corpo.append(el("div", { class: "modal-actions" }, concluir));
    };

    fecharBtn.addEventListener("click", () => {
      erro.textContent = "";
      const loteIds = painel.selecionados();
      if (loteIds.length === 0) return;
      fecharBtn.disabled = true;

      // resumo pelo diff, mesmo padrão de "Abrir faturas em massa" — o
      // comando não devolve motivo por OS pulada (a tela infere pelo diff,
      // mesmo espírito de "Abrir faturas em massa") — a situação atual da
      // própria OS pulada já entrega o motivo mais provável.
      const motivoDaPulada = (depois: Snapshot, id: Id): string => {
        try {
          const situacaoAtual = situacaoLote(depois, id);
          if (situacaoAtual === "faturado") return "A OS foi faturada antes de confirmar.";
          if (situacaoAtual === "cancelado") return "A OS foi cancelada antes de confirmar.";
        } catch {
          return "A OS foi excluída antes de confirmar.";
        }
        return "A situação da OS mudou antes de confirmar — abra-a e feche individualmente.";
      };
      void criarAcaoComando(store, erro)(
        { agregado: "devolucao", cmd: { tipo: "registrarEmMassa", loteIds } },
        null,
        () => {
          const depois = store.get();
          const fechadas = loteIds.filter(
            (id) => depois.lotes.find((l) => l.id === id)?.devolvidoEm != null,
          );
          const puladas = loteIds
            .filter((id) => !fechadas.includes(id))
            .map((id) => ({
              os: elegiveis.find((l) => l.id === id)?.os ?? 0,
              motivo: motivoDaPulada(depois, id),
            }));
          mostrarResumo(fechadas.length, puladas);
        },
        () => atualizar(),
      );
    });

    const cancelar = botao("secundario", {}, "Cancelar");
    cancelar.addEventListener("click", fechar);

    corpo.append(
      el(
        "p",
        { class: "muted" },
        "Cada OS fecha com a própria recebida: devolvida e conferida ficam " +
          "iguais a ela, sem retenção. Revise antes se alguma OS teve " +
          "divergência ou peça retida — feche essas individualmente.",
      ),
      el(
        "div",
        { class: "campo selecao-campo" },
        el("div", { class: "selecao-campo-cab" }, el("span", {}, "OS"), contador),
        painel.element,
      ),
      erro,
      el("div", { class: "modal-actions" }, cancelar, fecharBtn),
    );
    return corpo;
  });
}
